/**
 * @hidden
 */
export type EventFunction<TSender> = (e: EventArgs<TSender>) => any;

/**
 * ## Event arguments
 * 
 * Event arguments for triggered dms events. 
 * 
 * These are passed as the first argument in the registered function with the method "on" on both Grids and Controls. 
 * @param TSender The type for the sender.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // register event on control with Control ID 1337.
 * @@ctr:1337@@.on(DMS.Events.ControlEventTypes.Click, function(e) { 
 *      // e is instance of this event args class
 * });
 * ```
 */
export class EventArgs<TSender> {

    private _data: any;
    private _sender: TSender;
    private _defaultPrevented = false;

    private _defaultPreventedPromise: Promise<boolean>;

    /**
     * Stop the event execution. For example stops the command execution in a BeforeExecuteScript.
     * @since 4.0.0
     */
    public preventDefault(): void {
        this._defaultPrevented = true;
    }
    
    /**
     * Check if the event was cancelled.
     * @returns True if the event was cancelled.
     * @since 4.0.0
     */
    public isDefaultPrevented(): boolean {
        return this._defaultPrevented;
    }

    /**
     * Additional available data. This differs for every event type.
     * @see Please see {@link GridEventTypes} and {@link ControlEventTypes} for more infos.
     * @since 4.0.0
     */
    public get data(): any {
        return this._data;
    }

    /**
     * Set additional available data
     * @hidden
     */
    public set data(data: any) {
        this._data = data;
    }

    /**
     * The object that triggered the event.
     * 
     * This is the grid instance for grid events and the control instance for control events.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // register event on control with Control ID 1337
     * @@ctr:1337@@.on(DMS.Events.ControlEventTypes.Click, function(e) {
     *      // e.sender is the control instance.
     * });
     * 
     * // register event on filter on page
     * @@grd:GridName@@.on(DMS.Events.GridEventTypes.FilterCollapsed, function(e) {
     *      // e.sender is the grid instance.
     * });
     * ```
     */
    public get sender(): TSender {
        return this._sender;
    }

    /**
     * @hidden
     */
    public get defaultPreventedPromise(): Promise<boolean> {
        return this._defaultPreventedPromise;
    }

    /**
     * @hidden
     */
    public set defaultPreventedPromise(value: Promise<boolean>) {
        this._defaultPreventedPromise = value;
    }

    /**
     * Create new Eventarguments.
     * @param sender Sender of the event.
     * @param data Data.
     * @hidden
     */
    constructor(sender: TSender, data?: any) {
        this._sender = sender;
        this._data = data;
        this.defaultPreventedPromise = Promise.resolve(false);
    }
}